import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Tabs, Tab, Box, Alert, CircularProgress, Stack } from "@mui/material";
import { Card, FormGroup } from "@cofano/cck";

import useComments from "components/comments/useComments";
import CommentsWidget from "components/comments/CommentsWidget";

import CallContainersAmount from "view/calls/components/amount/CallContainersAmount";
import ContainerAmountSummary from "view/containers/components/summary/ContainerAmountSummary";
import LinkedPushedBargeCallsTable from "view/calls/components/table/LinkedPushedBargeCallsTable";
import CallSideViewerDocumentsBlock from "view/calls/components/sideviewer/blocks/CallSideViewerDocumentsBlock";
import CallSideViewerTimesheetBlock from "view/calls/components/sideviewer/blocks/CallSideViewerTimesheetBlock";

import { Language, hasPluginPermission, hasOptionalFieldPermission, Fields } from "imports/Utils";

import useCallSideviewer, { CargoActionFromCallSideviewer, CargoFromCallSideviewer } from "./useCallSideviewer";
import CallSideviewerTabs from "./tabs/CallSideviewerTabs";
import CallSideviewerCallBlock from "./blocks/CallSideviewerCallBlock";
import CallSideviewerVoyageBlock from "./blocks/CallSideviewerVoyageBlock";
import CallSideviewerCargoBlock from "./blocks/CallSideviewerCargoBlock";

import { BargeType, CallType } from "__generated__/enums";
import { GetVoyageCallsIdDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";

const CallSideviewer = ({
	defaultCallId,
	nextCallId,
	refetch,
	bargeType,
	isShipEditable,
	showFutureCalls = false,
	forceReadOnly = false,
}: {
	defaultCallId: number | null;
	nextCallId: number | null;
	refetch: (callId: number) => void;
	bargeType: BargeType | null;
	isShipEditable: boolean;
	showFutureCalls?: boolean;
	forceReadOnly?: boolean;
}) => {
	const session = useStoreState(state => state.session);
	const showTimesheet = hasPluginPermission(session, "cargoTimesheet");

	const [ loadingOrDischarging, setLoadingOrDischarging ] = useState<CallType>(CallType.Loading);
	const [ selectedAction, setSelectedAction ] = useState<CargoActionFromCallSideviewer | null>(null);
	const [ selectedCargo, setSelectedCargo ] = useState<CargoFromCallSideviewer | null>(null);
	const [ cargoLoading, setCargoLoading ] = useState(!hasPluginPermission(session, "useContainers"));

	const { selectedCall, calls, loading, fetchCall } = useCallSideviewer({
		defaultCallId: nextCallId ?? defaultCallId,
		showFutureCalls,
	});

	const linkedCalls = selectedCall?.linkedChildCalls ?? [];

	const [ getVoyage ] = useLazyQuery(GetVoyageCallsIdDocument);

	const { commentsProps, openComment } = useComments({
		cargos: selectedCall?.voyage?.cargos || [],
		calls: selectedCall ? [ { ...selectedCall } ] : [],
		onChange: () => {
			if (selectedCall?.id) {
				fetchCall(selectedCall.id);
			}
		},
	});

	if (loading && !selectedCall) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (!defaultCallId && !nextCallId) {
		return (
			<Box p={2}>
				<Alert severity="info">
					{intl.formatMessage({ id: "NO_CALLS_FOUND" })}
				</Alert>
			</Box>
		);
	}

	if (!selectedCall) {
		return null;
	}

	const isPushingTug = bargeType === BargeType.PusherTug;

	return (
		<React.Fragment>
			<div className="call-sideviewer" style={{ position: "relative" }}>
				{(cargoLoading || loading) && (
					<Box sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "rgba(244, 245, 247, 0.6)",
						zIndex: 200,
					}}>
						<CircularProgress size={24} />
					</Box>
				)}

				<Box
					p={2}
					sx={{
						backgroundColor: (theme) => theme.palette.mode === "dark" ? "#15171C" : "#F4F5F7",
						position: "sticky",
						top: 0,
						zIndex: 100,
					}}>
					<CallSideviewerTabs
						calls={calls}
						nextCallId={nextCallId}
						selectedCallId={selectedCall?.id ?? null}
						onChange={(callId: number) => {
							fetchCall(callId);
						}} />
				</Box>

				<Box p={2} pt={0}>
					<Stack spacing={2}>
						<CallSideviewerVoyageBlock
							call={selectedCall}
							isShipEditable={isShipEditable}
							forceReadOnly={forceReadOnly}
							refetch={(call, isRescheduled) => {
								if (isRescheduled) {
									getVoyage({
										variables: {
											id: call.voyage?.id,
										},
									}).then(({ data }) => {
										const firstCall = data?.voyage?.calls?.[0];

										if (firstCall) {
											fetchCall(firstCall.id);
											refetch(firstCall.id);
										}
									});
								} else {
									fetchCall(call.id);
									refetch(call.id);
								}
							}} />

						<CallSideviewerCallBlock
							call={selectedCall}
							forceReadOnly={forceReadOnly}
							refetch={(callId) => {
								fetchCall(callId);
								refetch(callId);
							}}
							openComment={openComment} />

						{isPushingTug ? (
							<Card title={intl.formatMessage({ id: "LINKED_PUSHED_BARGE_CALLS" })} disablePadding>
								<LinkedPushedBargeCallsTable linkedCalls={linkedCalls} />
							</Card>
						) : (
							<>
								{hasPluginPermission(session, "useContainers") ? (
									<div className="call-sideviewer-containers-amount">
										<Card title={Language("CONTAINER_INFORMATION")}>
											{!selectedCall?.voyage?.isBallast && hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos") && (
												<Box marginBottom={1}>
													<FormGroup
														labelPlacement="left-center"
														label={<b>{Language("EXPECTED_CONTAINERS")}</b>} />
													<ContainerAmountSummary
														loadingAmount={selectedCall?.expectedLoadingAmount ?? null}
														dischargingAmount={selectedCall?.expectedDischargingAmount ?? null}
														loadingTeu={selectedCall?.expectedLoadingTeu ?? null}
														dischargingTeu={selectedCall?.expectedDischargingTeu ?? null}
													/>
												</Box>
											)}

											<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 1 }}>
												<Tabs
													value={loadingOrDischarging}
													onChange={(_, value) => setLoadingOrDischarging(value)}
													indicatorColor="primary">
													<Tab label={Language("CARGO_LOADING")} value={CallType.Loading} />
													<Tab label={Language("CARGO_DISCHARGING")} value={CallType.Discharging} />
												</Tabs>
											</Box>

											<CallContainersAmount
												call={selectedCall}
												loadingOrDischarging={loadingOrDischarging} />
										</Card>
									</div>
								) : (
									<CallSideviewerCargoBlock
										call={selectedCall}
										forceReadOnly={forceReadOnly}
										openComment={openComment}
										onActionChange={(action, cargo) => {
											setSelectedAction(action);
											setSelectedCargo(cargo);
										}}
										onLoadingChange={setCargoLoading} />
								)}
							</>
						)}

						<CallSideViewerDocumentsBlock
							call={selectedCall}
							selectedAction={selectedAction}
							cargoId={selectedCargo?.id ?? null}
							forceReadOnly={forceReadOnly}
							refetch={() => fetchCall(selectedCall.id)}
						/>

						{showTimesheet && (
							<CallSideViewerTimesheetBlock
								selectedAction={selectedAction}
								cargo={selectedCargo}
								forceReadOnly={forceReadOnly}
								refetch={() => fetchCall(selectedCall.id)}
							/>
						)}
					</Stack>
				</Box>
			</div>

			<CommentsWidget {...commentsProps} />
		</React.Fragment>
	);
};

export default CallSideviewer;
